import * as React from "react";
import { createPortal } from "react-dom";
import { CircleX } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import clsx from "clsx";
import * as s from "./Modal.style.css";
import { baseTheme } from "../../styles/baseTheme";
import type { ModalProps, ModalSectionProps } from "./Modal.types";
import { useT } from "../../../app/lib/i18n";
import { getPortalContainer } from "../../lib/portalContainer";

export type { ModalProps, ModalSectionProps } from "./Modal.types";

type ModalChromeContextValue = {
  /** Marks this modal as using Header/Body/Footer pinned layout. */
  enablePinnedLayout: () => void;
};

const ModalChromeContext = React.createContext<ModalChromeContextValue | null>(
  null,
);

function usePinnedLayoutRegistration() {
  const ctx = React.useContext(ModalChromeContext);
  React.useLayoutEffect(() => {
    ctx?.enablePinnedLayout();
  }, [ctx]);
}

function ModalHeader({ className, children, ...rest }: ModalSectionProps) {
  usePinnedLayoutRegistration();
  return (
    <div className={clsx(s.modalHeader, className)} {...rest}>
      {children}
    </div>
  );
}

function ModalBody({ className, children, ...rest }: ModalSectionProps) {
  usePinnedLayoutRegistration();
  return (
    <div className={clsx(s.modalBody, className)} {...rest}>
      {children}
    </div>
  );
}

function ModalFooter({ className, children, ...rest }: ModalSectionProps) {
  usePinnedLayoutRegistration();
  return (
    <div className={clsx(s.modalFooter, className)} {...rest}>
      {children}
    </div>
  );
}

/** Use around Header/Body/Footer when the modal content is a `<form>`. */
function ModalForm({
  className,
  children,
  ...rest
}: React.FormHTMLAttributes<HTMLFormElement>) {
  usePinnedLayoutRegistration();
  return (
    <form className={clsx(s.modalFormShell, className)} {...rest}>
      {children}
    </form>
  );
}

/** Open modals in the order they opened; the last entry is the topmost. */
const openModalStack: object[] = [];

function ModalRoot({
  children,
  open,
  setOpen,
  takeoverAtWidth = 768,
  closeOnClickOff = true,
  darkenBackground = true,
  minWidth = "520px",
  minHeight = "200px",
  maxWidth = "90vw",
  maxHeight = "90vh",
  container,
}: ModalProps) {
  const t = useT();
  const [isTakeover, setIsTakeover] = React.useState(false);
  const [pinnedLayout, setPinnedLayout] = React.useState(false);

  const chromeValue = React.useMemo<ModalChromeContextValue>(
    () => ({
      enablePinnedLayout: () => setPinnedLayout(true),
    }),
    [],
  );

  React.useEffect(() => {
    if (!open) setPinnedLayout(false);
  }, [open]);

  React.useEffect(() => {
    const checkViewport = () => {
      setIsTakeover(window.innerWidth < takeoverAtWidth);
    };

    checkViewport();
    window.addEventListener("resize", checkViewport);
    return () => window.removeEventListener("resize", checkViewport);
  }, [takeoverAtWidth]);

  const stackToken = React.useRef({});

  React.useEffect(() => {
    if (!open) return;
    const token = stackToken.current;
    openModalStack.push(token);

    // Only the topmost open modal closes on Escape, so a modal opened from
    // another modal doesn't take its parent down with it.
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape" && openModalStack[openModalStack.length - 1] === token) {
        setOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
      const index = openModalStack.lastIndexOf(token);
      if (index !== -1) openModalStack.splice(index, 1);
    };
  }, [open, setOpen]);

  React.useEffect(() => {
    if (open) {
      // Lock both html and body — some layouts (e.g. certification-app) put
      // the real scroll container on documentElement rather than body, or
      // vice versa, so lock whichever is actually scrolling.
      const originalBodyOverflow = document.body.style.overflow;
      const originalHtmlOverflow = document.documentElement.style.overflow;
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = originalBodyOverflow;
        document.documentElement.style.overflow = originalHtmlOverflow;
      };
    }
  }, [open]);

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!closeOnClickOff) return;
    if (e.target === e.currentTarget) {
      setOpen(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
    exit: { opacity: 0 },
  };

  const modalVariants = {
    hidden: {
      opacity: 0,
      scale: 0.95,
    },
    visible: {
      opacity: 1,
      scale: 1,
      transition: {
        duration: 0.2,
        ease: [0.16, 1, 0.3, 1],
      },
    },
    exit: {
      opacity: 0,
      scale: 0.95,
      transition: {
        duration: 0.15,
        ease: [0.16, 1, 0.3, 1],
      },
    },
  };

  const modalContent = (
    <AnimatePresence mode="wait">
      {open && (
        <motion.div
          className={clsx(
            s.backdrop,
            darkenBackground ? s.backdropDark : s.backdropTransparent,
            isTakeover && s.backdropTakeover,
          )}
          onClick={handleBackdropClick}
          variants={backdropVariants}
          initial="hidden"
          animate={{
            ...backdropVariants.visible,
            padding: isTakeover ? 0 : baseTheme.space[4],
          }}
          exit="exit"
          layout
          transition={{
            layout: {
              duration: 0.3,
              ease: [0.16, 1, 0.3, 1],
            },
            opacity: { duration: 0.2 },
            padding: {
              duration: 0.3,
              ease: [0.16, 1, 0.3, 1],
            },
          }}
          role="presentation"
        >
          <motion.div
            className={clsx(s.modalContainer, isTakeover && s.modalTakeover)}
            style={{
              minWidth: isTakeover ? undefined : minWidth,
              minHeight: isTakeover ? undefined : minHeight,
              maxWidth: isTakeover ? undefined : maxWidth,
              maxHeight: isTakeover ? undefined : maxHeight,
            }}
            variants={modalVariants}
            initial="hidden"
            animate={{
              ...modalVariants.visible,
              // Dynamic viewport units — see modalTakeover in Modal.style.css.ts
              // for why plain vw/vh cut off content on mobile.
              width: isTakeover ? "100dvw" : "auto",
              // Leave height unset outside takeover so maxHeight can create a
              // definite size — required for Header/Body/Footer body scrolling.
              ...(isTakeover ? { height: "100dvh" } : {}),
              borderRadius: isTakeover ? 0 : baseTheme.radius.md,
            }}
            exit="exit"
            layout
            transition={{
              layout: {
                duration: 0.3,
                ease: [0.16, 1, 0.3, 1],
              },
              borderRadius: {
                duration: 0.3,
                ease: [0.16, 1, 0.3, 1],
              },
            }}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <button
              className={s.closeButton}
              onClick={handleClose}
              aria-label={t("shared.ui.closeModal")}
              type="button"
            >
              <CircleX size={20} />
            </button>
            <ModalChromeContext.Provider value={chromeValue}>
              <div
                className={clsx(
                  s.modalContent,
                  pinnedLayout ? s.modalContentPinned : s.modalContentLegacy,
                  isTakeover && s.modalContentTakeover,
                )}
              >
                {children}
              </div>
            </ModalChromeContext.Provider>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  const portalContainer = container ?? getPortalContainer();
  return createPortal(modalContent, portalContainer);
}

export const Modal = Object.assign(ModalRoot, {
  Header: ModalHeader,
  Body: ModalBody,
  Footer: ModalFooter,
  Form: ModalForm,
});
